import { useEffect, useRef, useState } from "react";
import { useSearchModal } from "../../stores/useSearchProvider";
import { ITiendaProductos } from "@/types/app";
import Product from "../product/product";
import axios from "axios";
import { capturarError } from "@/utils/global";
import CakeLoader from "../loaders/cakeLoader";
import {
    MagnifyingGlass,
    X
} from "@phosphor-icons/react/dist/ssr";

const SimpleSearch = () => {
    const { isSearchModalOpen, closeSearchModal } = useSearchModal();

    // Estados principales
    const [query, setQuery] = useState("");
    const [products, setProducts] = useState<ITiendaProductos[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [sortBy, setSortBy] = useState<string>('price_asc');
    const [popularProducts, setPopularProducts] = useState<string[]>([]);

    // Referencias
    const inputRef = useRef<HTMLInputElement>(null);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Función para ordenar productos
    const sortProducts = (products: ITiendaProductos[], sortType: string): ITiendaProductos[] => {
        const sortedProducts = [...products];

        switch (sortType) {
            case 'price_asc':
                return sortedProducts.sort((a, b) => (a.precio || 0) - (b.precio || 0));
            case 'price_desc':
                return sortedProducts.sort((a, b) => (b.precio || 0) - (a.precio || 0));
            case 'name_asc':
                return sortedProducts.sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''));
            case 'name_desc':
                return sortedProducts.sort((a, b) => (b.nombre || '').localeCompare(a.nombre || ''));
            default:
                return sortedProducts; // Mantener orden original (relevancia)
        }
    };

    // Función para obtener productos populares
    const getPopularProducts = async () => {
        try {
            const response = await axios.get(`${window.location.origin}/api/terminos-populares`);
            if (response.status === 200) {
                const terms = response.data || [];
                setPopularProducts(terms);
            }
        } catch (error) {
            console.log('Error getting popular terms:', error);
            // Fallback: términos populares por defecto
            setPopularProducts(['torta', 'chocolate', 'vainilla', 'fresa', 'coctel', 'personalizada', 'cupcakes', 'brownie']);
        }
    };

    // Función para realizar búsqueda usando la API original
    const performSearch = async (searchTerm: string) => {
        if (searchTerm.length < 2) {
            setProducts([]);
            setHasSearched(false);
            return;
        }

        setIsLoading(true);
        setHasSearched(true);

        try {
            const url = `${window.location.origin}/api/search`;
            const response = await axios.post(url, {
                busqueda: searchTerm
            });

            if (response.status === 200) {
                const results = response.data.data || [];
                const sortedResults = sortProducts(results, sortBy);
                setProducts(sortedResults);
            }
        } catch (error) {
            console.log('Search error:', error);
            setProducts([]);
            capturarError(error);
        } finally {
            setIsLoading(false);
        }
    };

    // Efecto para manejar búsqueda con debounce
    useEffect(() => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        if (query.length >= 2) {
            debounceRef.current = setTimeout(() => {
                performSearch(query);
            }, 300);
        } else if (query.length === 0) {
            setProducts([]);
            setHasSearched(false);
        }

        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
    }, [query]);

    // Enfocar input y cargar datos cuando se abre el modal
    useEffect(() => {
        if (isSearchModalOpen && inputRef.current) {
            inputRef.current.focus();
            getPopularProducts();
        }
    }, [isSearchModalOpen]);

    // Manejar el scroll del body
    useEffect(() => {
        if (isSearchModalOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "unset";
        }
        return () => {
            document.body.style.overflow = "unset";
        };
    }, [isSearchModalOpen]);

    // Reordenar productos cuando cambie el filtro
    useEffect(() => {
        if (products.length > 0) {
            const sortedResults = sortProducts(products, sortBy);
            setProducts(sortedResults);
        }
    }, [sortBy]);

    // Manejar teclas especiales
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            closeSearchModal();
        } else if (e.key === 'Enter') {
            if (query.trim()) {
                performSearch(query);
            }
        }
    };

    // Limpiar búsqueda
    const clearSearch = () => {
        setQuery("");
        setProducts([]);
        setHasSearched(false);
        if (inputRef.current) {
            inputRef.current.focus();
        }
    };

    // Función para manejar click en chip de sugerencia
    const handleChipClick = (chipText: string) => {
        setQuery(chipText);
        performSearch(chipText);
    };

    return (
        <div
            className={`fixed inset-0 bg-black/70 z-[9999] transition-all duration-300 ${isSearchModalOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
                }`}
            onClick={closeSearchModal}
        >
            {isLoading && <CakeLoader />}

            <div
                className={`fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full max-w-4xl max-h-[85vh] sm:max-h-[90vh] bg-white rounded-2xl shadow-2xl transition-all duration-300 ${isSearchModalOpen ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
                    }`}
                style={{
                    left: '50%',
                    transform: 'translateX(-50%) translateY(-50%)',
                    width: window.innerWidth < 640 ? 'calc(100% - 32px)' : '100%',
                    maxWidth: window.innerWidth < 640 ? 'calc(100% - 32px)' : '56rem'
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
                    <div className="flex items-center gap-3">
                        <MagnifyingGlass size={24} className="text-[#790546]" />
                        <h2 className="text-xl font-semibold text-gray-800">Búsqueda</h2>
                    </div>
                    <button
                        onClick={closeSearchModal}
                        className="w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors"
                    >
                        <X size={20} className="text-gray-600" />
                    </button>
                </div>

                {/* Contenido */}
                <div className="p-4 sm:p-6 overflow-y-auto max-h-[calc(85vh-140px)] sm:max-h-[calc(90vh-140px)]">
                    {/* Barra de búsqueda */}
                    <div className="relative mb-4 sm:mb-6">
                        <div className="relative">
                            <input
                                ref={inputRef}
                                type="text"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder="Buscar productos..."
                                className="w-full h-14 pl-6 pr-12 py-4 text-lg border-2 border-gray-200 rounded-xl focus:border-[#790546] focus:outline-none transition-colors"
                            />
                            <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                                {query && (
                                    <button
                                        onClick={clearSearch}
                                        className="w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors"
                                    >
                                        <X size={16} className="text-gray-600" />
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Chips de sugerencias */}
                    {!hasSearched && popularProducts.length > 0 && (
                        <div className="mb-4 sm:mb-6">
                            <h3 className="text-sm font-medium text-gray-700 mb-3">Más vendidos</h3>
                            <div className="flex flex-wrap gap-2">
                                {popularProducts.map((product, index) => (
                                    <button
                                        key={index}
                                        onClick={() => handleChipClick(product)}
                                        className="px-3 py-2 bg-white border border-gray-200 rounded-full text-sm text-gray-700 hover:border-[#790546] hover:text-[#790546] transition-colors cursor-pointer"
                                    >
                                        {product}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Filtros */}
                    {hasSearched && products.length > 0 && (
                        <div className="mb-4 sm:mb-6 p-4 bg-gray-50 rounded-xl">
                            <div className="flex flex-col sm:flex-row gap-4">
                                <div className="flex-1">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Ordenar por</label>
                                    <select
                                        value={sortBy}
                                        onChange={(e) => setSortBy(e.target.value)}
                                        className="w-full p-2 border border-gray-200 rounded-lg focus:border-[#790546] focus:outline-none"
                                    >
                                        <option value="price_asc">Precio: Menor a Mayor</option>
                                        <option value="price_desc">Precio: Mayor a Menor</option>
                                        <option value="name_asc">Nombre: A-Z</option>
                                        <option value="name_desc">Nombre: Z-A</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Resultados */}
                    {hasSearched && (
                        <div>
                            {products.length > 0 ? (
                                <div>
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="text-lg font-semibold text-gray-800">
                                            {products.length} producto{products.length !== 1 ? 's' : ''} encontrado{products.length !== 1 ? 's' : ''}
                                        </h3>
                                        <span className="text-sm text-gray-500">
                                            Resultados para "{query}"
                                        </span>
                                    </div>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4 lg:gap-6">
                                        {products.map((product, index) => (
                                            <div key={`${product.id}-${index}`} className="transform scale-90 sm:scale-100">
                                                <Product data={product} />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center py-12">
                                    <MagnifyingGlass size={48} className="text-gray-300 mx-auto mb-4" />
                                    <h3 className="text-lg font-medium text-gray-600 mb-2">
                                        No se encontraron productos
                                    </h3>
                                    <p className="text-gray-500">
                                        Intenta con otros términos de búsqueda o revisa la ortografía
                                    </p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Estado inicial */}
                    {!hasSearched && (
                        <div className="text-center py-12">
                            <MagnifyingGlass size={48} className="text-gray-300 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-600 mb-2">
                                Busca productos
                            </h3>
                            <p className="text-gray-500">
                                Encuentra exactamente lo que necesitas
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SimpleSearch;
